/**
 * CyclesTable
 *
 * Shared render component for the diligence cycles table.
 * Used in:
 *   - VSME diligence workbench (CyclesList.tsx)
 *   - Accounting Hub mission portal (MissionCyclesTable.tsx)
 *
 * The caller is responsible for fetching and sorting cycle data; this component
 * handles only layout and display.
 */

import { useMemo, type ReactNode } from 'react';
import { __ } from '../i18n/vsme';
import CircularProgress from './CircularProgress';
import StatusIndicatorBadges from './StatusIndicatorBadges';
import type { DiligenceStatus } from './StatusBadge';

/** Diligence status counts — matches DiligenceStats in diligence app and MissionCycleDiligenceStats in portal. */
export type CycleStatsData = {
  total: number;
  todo: number;
  revise: number;
  supervise: number;
  non_applicable: number;
  anomalie: number;
};

export type CycleRowData = {
  num: number;
  name: string;
  stats: CycleStatsData;
  formCompletionPercentage: number;
  firstStepId?: string | null;
};

type CyclesTableProps = {
  cycles: CycleRowData[];
  /** CSS classes applied to the <table> element (e.g. `diligcycles__table container-style`). */
  tableClassName?: string;
  /** Called when the diligence row action button is clicked, with the cycle number. */
  onCycleAction?: (cycleNum: number) => void;
  /** Label for the diligence row action button. Defaults to "Contrôler les diligences". */
  actionLabel?: string;
  /** Called when the optional form row action button is clicked. */
  onCycleFormAction?: (cycle: CycleRowData) => void;
  /** Label for the optional form row action button. Defaults to "Continuer le formulaire". */
  formActionLabel?: string;
  /** CSS class for the actions cell wrapper (e.g. `mission-cycles__actions`). */
  actionsCellClassName?: string;
  /** Custom actions renderer; when set, replaces the default tertiary buttons. */
  renderActions?: (cycle: CycleRowData) => ReactNode;
};

const INDICATOR_STATUSES: DiligenceStatus[] = ['todo', 'revise', 'supervise'];

const CyclesTable = ({
  cycles,
  tableClassName = '',
  onCycleAction,
  actionLabel,
  onCycleFormAction,
  formActionLabel,
  actionsCellClassName = '',
  renderActions,
}: CyclesTableProps) => (
  <table className={`thn-table ${tableClassName}`.trim()}>
    <colgroup>
      <col style={{ width: '22%' }} />
      <col style={{ width: '18%' }} />
      <col style={{ width: '38%' }} />
      <col style={{ width: '22%' }} />
    </colgroup>
    <thead>
      <tr>
        <th className="table__cell h3">{__('Cycle')}</th>
        <th className="table__cell h3">{__('Complétion formulaire')}</th>
        <th className="table__cell h3">{__('Diligences')}</th>
        <th className="table__cell h3">{__('Actions')}</th>
      </tr>
    </thead>
    <tbody>
      {cycles.map((cycle) => (
        <CycleTableRow
          key={cycle.num}
          cycle={cycle}
          onCycleAction={onCycleAction}
          actionLabel={actionLabel ?? __('Contrôler les diligences')}
          onCycleFormAction={onCycleFormAction}
          formActionLabel={formActionLabel ?? __('Continuer le formulaire')}
          actionsCellClassName={actionsCellClassName}
          renderActions={renderActions}
        />
      ))}
    </tbody>
  </table>
);

type CycleTableRowProps = {
  cycle: CycleRowData;
  onCycleAction?: (cycleNum: number) => void;
  actionLabel: string;
  onCycleFormAction?: (cycle: CycleRowData) => void;
  formActionLabel: string;
  actionsCellClassName: string;
  renderActions?: (cycle: CycleRowData) => ReactNode;
};

const CycleTableRow = ({
  cycle,
  onCycleAction,
  actionLabel,
  onCycleFormAction,
  formActionLabel,
  actionsCellClassName,
  renderActions,
}: CycleTableRowProps) => {
  const indicators = useMemo(
    () =>
      INDICATOR_STATUSES
        .filter((s) => cycle.stats[s] > 0)
        .map((s) => ({ status: s, count: cycle.stats[s] })),
    [cycle.stats],
  );

  return (
    <tr className="table__row">
      <td className="table__cell">
        <span className="table__cycle-name">{cycle.name}</span>
      </td>
      <td className="table__cell">
        <CircularProgress percentage={cycle.formCompletionPercentage} size={60} strokeWidth={2} />
      </td>
      <td className="table__cell">
        <StatusIndicatorBadges indicators={indicators} className="dilig-table__stats" />
      </td>
      <td className="table__cell">
        {renderActions !== undefined ? (
          renderActions(cycle)
        ) : (
          <div className={actionsCellClassName}>
            {onCycleFormAction !== undefined ? (
              <button
                type="button"
                className="table__button btn-tertiary"
                onClick={() => onCycleFormAction(cycle)}
              >
                {formActionLabel}
              </button>
            ) : null}
            <button type="button" className="table__button btn-tertiary" onClick={() => onCycleAction?.(cycle.num)}>
              {actionLabel}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

export default CyclesTable;
